import React, { useState } from 'react';
import {
  ChevronDown, BookOpen, FileText, Users,
  Target, DollarSign, AlertTriangle, CheckSquare, ClipboardList
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useInView from '../components/useInView';
import CTABanner from '../components/CTABanner';

const AccordionSection = ({ section, index, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [ref, inView] = useInView();

  return (
    <div
      ref={ref}
      style={{
        background: 'rgba(18,22,36,0.7)',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${open ? section.color + '50' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'border-color 0.3s ease',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transitionProperty: 'opacity, transform, border-color',
        transitionDuration: '0.6s',
        transitionDelay: `${index * 0.1}s`,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '24px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: `${section.color}18`,
          border: `1px solid ${section.color}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: section.color,
          flexShrink: 0,
          fontSize: '20px',
          fontWeight: 800,
        }}>
          {index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: section.color, fontWeight: 600, letterSpacing: '1px', marginBottom: '4px' }}>
            {section.tag}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
            {section.title}
          </div>
        </div>
        <ChevronDown
          size={20}
          style={{
            color: 'rgba(255,255,255,0.3)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s ease',
            flexShrink: 0,
          }}
        />
      </button>

      {/* Content */}
      <div style={{
        maxHeight: open ? '800px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <div style={{ padding: '0 24px 24px' }}>
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.05)',
            paddingTop: '20px',
          }}>
            {/* Topics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {section.topics.map((topic, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.025)',
                    borderRadius: '10px',
                    animation: open ? `fadeInUp 0.3s ease ${i * 0.05}s both` : 'none',
                  }}
                >
                  <div style={{ color: section.color, flexShrink: 0 }}>
                    {topic.icon}
                  </div>
                  <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
                    {topic.label}
                  </span>
                  {topic.ref && (
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.3)',
                      fontFamily: 'monospace',
                      flexShrink: 0,
                    }}>
                      {topic.ref}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Task badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: `linear-gradient(135deg, ${section.color}18, ${section.color}08)`,
              border: `1px solid ${section.color}30`,
              borderRadius: '12px',
            }}>
              <CheckSquare size={18} style={{ color: section.color, flexShrink: 0 }} />
              <div>
                <span style={{ fontSize: '11px', color: section.color, fontWeight: 700, display: 'block', marginBottom: '2px' }}>
                  ПРАКТИЧЕСКОЕ ЗАДАНИЕ
                </span>
                <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                  {section.task}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgramPage = () => {
  const { t } = useLang();
  const [headerRef, headerInView] = useInView();

  const sections = [
    {
      title: t('sec1_title'),
      tag: 'ОСНОВЫ',
      color: '#2E5AFF',
      task: t('sec1_task'),
      topics: [
        { icon: <BookOpen size={16} />, label: t('sec1_t1'), ref: 'ГОСТ Р 54869-2011' },
        { icon: <Target size={16} />, label: t('sec1_t2') },
        { icon: <Users size={16} />, label: t('sec1_t3') },
      ],
    },
    {
      title: t('sec2_title'),
      tag: 'ИНИЦИАЦИЯ',
      color: '#5B8AFF',
      task: t('sec2_task'),
      topics: [
        { icon: <FileText size={16} />, label: t('sec2_t1'), ref: 'п. 5.2' },
        { icon: <ClipboardList size={16} />, label: t('sec2_t2') },
        { icon: <Users size={16} />, label: t('sec2_t3') },
      ],
    },
    {
      title: t('sec3_title'),
      tag: 'ПЛАНИРОВАНИЕ',
      color: '#8A2BE2',
      task: t('sec3_task'),
      topics: [
        { icon: <Target size={16} />, label: t('sec3_t1'), ref: 'п. 5.3.1' },
        { icon: <ClipboardList size={16} />, label: t('sec3_t2'), ref: 'п. 5.3.2' },
        { icon: <DollarSign size={16} />, label: t('sec3_t3'), ref: 'п. 5.3.3' },
        { icon: <Users size={16} />, label: t('sec3_t4'), ref: 'пп. 5.3.4, 5.3.7' },
        { icon: <AlertTriangle size={16} />, label: t('sec3_t5'), ref: 'п. 5.3.6' },
      ],
    },
    {
      title: t('sec4_title'),
      tag: 'УПРАВЛЕНИЕ РИСКАМИ',
      color: '#C050FF',
      task: t('sec4_task'),
      topics: [
        { icon: <AlertTriangle size={16} />, label: t('sec4_t1') },
        { icon: <FileText size={16} />, label: t('sec4_t2') },
        { icon: <CheckSquare size={16} />, label: t('sec4_t3') },
      ],
    },
    {
      title: t('sec5_title'),
      tag: 'РЕАЛИЗАЦИЯ И ЗАКРЫТИЕ',
      color: '#FF3B9A',
      task: t('sec5_task'),
      topics: [
        { icon: <ClipboardList size={16} />, label: t('sec5_t1'), ref: 'пп. 5.4, 5.5' },
        { icon: <CheckSquare size={16} />, label: t('sec5_t2'), ref: 'п. 5.6' },
        { icon: <BookOpen size={16} />, label: t('sec5_t3') },
      ],
    },
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px' }}>
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '60px 24px 48px',
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '2px', background: 'linear-gradient(90deg, #2E5AFF, #8A2BE2)' }} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#2E5AFF', letterSpacing: '3px', textTransform: 'uppercase' }}>
            Curriculum
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'end' }}>
          <div>
            <h1 style={{
              fontSize: 'clamp(28px, 4vw, 48px)',
              fontWeight: 900,
              letterSpacing: '-1.5px',
              lineHeight: 1.1,
              color: '#fff',
            }}>
              {t('program_title').split(' ')[0]}{' '}
              <span style={{
                background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2, #FF3B9A)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {t('program_title').split(' ').slice(1).join(' ')}
              </span>
            </h1>
          </div>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
            {t('program_sub')}
          </p>
        </div>

        {/* Progress indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '40px',
          background: 'rgba(18,22,36,0.6)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px',
          padding: '16px 24px',
          flexWrap: 'wrap',
        }}>
          {sections.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '28px', height: '28px',
                  borderRadius: '50%',
                  background: `${s.color}22`,
                  border: `2px solid ${s.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 800, color: s.color,
                }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', display: 'none' }}>
                  {s.tag}
                </span>
              </div>
              {i < sections.length - 1 && (
                <div style={{
                  flex: 1,
                  minWidth: '20px',
                  height: '1px',
                  background: 'rgba(255,255,255,0.08)',
                }} />
              )}
            </React.Fragment>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
            5 разделов · 5 заданий
          </div>
        </div>
      </div>

      {/* Accordion sections */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 24px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}>
        {sections.map((section, i) => (
          <AccordionSection
            key={i}
            section={section}
            index={i}
            defaultOpen={i === 0}
          />
        ))}
      </div>

      <CTABanner />
    </div>
  );
};

export default ProgramPage;
